import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';
import {
  Search,
  Filter,
  Download,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  User,
  Award,
  DollarSign,
  Calendar,
  Mail,
  Loader2,
  Eye,
  Shield,
  TrendingUp,
  History,
  Check,
  X
} from 'lucide-react';

/* ───────── Types ───────── */
type Tab = 'progress' | 'claimed' | 'approved' | 'rejected';

interface Row {
  _id: string;
  email: string;
  rank: string;
  rewardType: 'silverReward' | 'goldReward' | 'platinumReward';
  reward: {
    rewardAmount: string;
    status: 'pending' | 'processing' | 'approved' | 'rejected';
    isClaimed: boolean;
    createdAt: string;
    updatedAt: string;
    approvedDate?: string;
    claimedDate?: string;
  };
}

interface Toast {
  type: 'success' | 'error' | 'warning';
  message: string;
  id: number;
}

/* ───────── Constants ───────── */
const API = `${API_BASE}/api/rank`;

/* ───────── Debounce ───────── */
function useDebounce<T>(value: T, delay = 400): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const RankDashboard: React.FC = () => {
  /* data */
  const [rows, setRows] = useState<Row[]>([]);
  const [tab, setTab] = useState<Tab>('progress');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  /* filters */
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [rewardTypeFilter, setRewardTypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const debouncedSearch = useDebounce(search);

  /* toast */
  const [toasts, setToasts] = useState<Toast[]>([]);
  const showToast = (type: Toast['type'], message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, { type, message, id }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(toast => toast.id !== id));
    }, 3000);
  };

  /* confirm modal */
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'approve' | 'reject' | null>(null);
  const [selectedRow, setSelectedRow] = useState<Row | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  /* ───────── Fetch Data ───────── */
  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/dashboard`, {
        params: {
          tab,
          page,
          search: debouncedSearch || undefined,
          status: statusFilter || undefined,
          rewardType: rewardTypeFilter || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined
        }
      });
      setRows(data.data || []);
      setTotalPages(data.totalPages || 1);
    } catch {
      setRows([]);
      showToast('error', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [tab, page, debouncedSearch, statusFilter, rewardTypeFilter, startDate, endDate]);

  const handleTabChange = (t: Tab) => {
    setTab(t);
    setPage(1);
    setRows([]);
  };

  /* ───────── Export CSV ───────── */
  const exportCSV = () => {
    const params = new URLSearchParams({
      tab,
      search: debouncedSearch || '',
      status: statusFilter || '',
      rewardType: rewardTypeFilter || '',
      startDate: startDate || '',
      endDate: endDate || ''
    });
    window.open(`${API}/dashboard/export?${params.toString()}`, '_blank');
    showToast('success', 'Export started successfully');
  };

  /* ───────── Reward Action ───────── */
  const rewardAction = async (
    row: Row,
    action: 'approve' | 'reject'
  ) => {
    try {
      setActionLoading(true);
      await axios.post(`${API}/reward-action`, {
        userId: row._id,
        rewardType: row.rewardType,
        action
      });
      showToast('success', `Reward ${action}d successfully`);
      fetchData(); // Refresh data
    } catch {
      showToast('error', `Failed to ${action} reward`);
    } finally {
      setActionLoading(false);
    }
  };

  const openConfirm = (row: Row, action: 'approve' | 'reject') => {
    setSelectedRow(row);
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  const confirmAndExecute = async () => {
    if (!selectedRow || !confirmAction) return;
    await rewardAction(selectedRow, confirmAction);
    setConfirmOpen(false);
    setSelectedRow(null);
    setConfirmAction(null);
  };

  /* ───────── Date Formatter ───────── */
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /* ───────── UI Components ───────── */
  const StatusBadge = ({ status }: { status: string }) => {
    const config = {
      pending: { icon: Clock, color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
      processing: { icon: Loader2, color: 'bg-blue-50 text-blue-700 border-blue-200' },
      approved: { icon: CheckCircle, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
      rejected: { icon: XCircle, color: 'bg-rose-50 text-rose-700 border-rose-200' }
    }[status] || { icon: AlertCircle, color: 'bg-gray-50 text-gray-700 border-gray-200' };

    const Icon = config.icon;

    return (
      <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border ${config.color}`}>
        <Icon size={14} />
        <span className="text-xs font-medium capitalize">{status}</span>
      </div>
    );
  };

  const RewardTypeBadge = ({ type }: { type: string }) => {
    const config = {
      silverReward: { label: 'Silver', color: 'bg-gray-100 text-gray-800' },
      goldReward: { label: 'Gold', color: 'bg-amber-100 text-amber-800' },
      platinumReward: { label: 'Platinum', color: 'bg-slate-100 text-slate-800' }
    }[type] || { label: type, color: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const DateInfo = ({ label, date }: { label: string; date?: string }) => {
    if (!date) return null;

    return (
      <div className="flex items-start gap-2">
        <History size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-sm font-medium text-gray-900 truncate">
            {formatDate(date)}
          </p>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'progress', label: 'In Progress', icon: TrendingUp },
    { id: 'claimed', label: 'Claimed', icon: Eye },
    { id: 'approved', label: 'Approved', icon: CheckCircle },
    { id: 'rejected', label: 'Rejected', icon: XCircle }
  ] as const;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-50 space-y-2 w-80 md:w-96">
        {toasts.map(toast => (
          <div
            key={toast.id}
            className={`flex items-center gap-3 p-4 rounded-xl shadow-lg backdrop-blur-sm border transform transition-all duration-300 ${toast.type === 'success'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
              : toast.type === 'error'
                ? 'bg-rose-50 border-rose-200 text-rose-800'
                : 'bg-amber-50 border-amber-200 text-amber-800'
              }`}
          >
            {toast.type === 'success' ? (
              <CheckCircle className="flex-shrink-0" />
            ) : toast.type === 'error' ? (
              <XCircle className="flex-shrink-0" />
            ) : (
              <AlertCircle className="flex-shrink-0" />
            )}
            <span className="text-sm font-medium">{toast.message}</span>
          </div>
        ))}
      </div>

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                Rank Reward Dashboard
              </h1>
              <p className="text-gray-600 mt-1">Manage and track reward claims</p>
            </div>
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Download size={18} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-5 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by email..."
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
              />
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center gap-2 px-5 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
            >
              <Filter size={18} />
              Filters
              {showFilters ? ' (Hide)' : ''}
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-gray-100">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  value={statusFilter}
                  onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reward Type</label>
                <select
                  className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  value={rewardTypeFilter}
                  onChange={e => { setRewardTypeFilter(e.target.value); setPage(1); }}
                >
                  <option value="">All Rewards</option>
                  <option value="silverReward">Silver</option>
                  <option value="goldReward">Gold</option>
                  <option value="platinumReward">Platinum</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="date"
                    className="w-full pl-12 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    value={startDate}
                    onChange={e => { setStartDate(e.target.value); setPage(1); }}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <div className="relative">
                  <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <input
                    type="date"
                    className="w-full pl-12 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    value={endDate}
                    onChange={e => { setEndDate(e.target.value); setPage(1); }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id as Tab)}
              className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all duration-300 ${tab === id
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
            >
              <Icon size={18} />
              <span className="font-medium">{label}</span>
              {rows.filter(r => {
                if (id === 'claimed') return r.reward.status === 'processing';
                if (id === 'approved') return r.reward.status === 'approved';
                if (id === 'rejected') return r.reward.status === 'rejected';
                if (id === 'progress') return ['pending', 'processing'].includes(r.reward.status);
                return false;
              }).length > 0 && (
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${tab === id ? 'bg-white/20' : 'bg-gray-100'
                    }`}>
                    {rows.filter(r => {
                      if (id === 'claimed') return r.reward.status === 'processing';
                      if (id === 'approved') return r.reward.status === 'approved';
                      if (id === 'rejected') return r.reward.status === 'rejected';
                      if (id === 'progress') return ['pending', 'processing'].includes(r.reward.status);
                      return false;
                    }).length}
                  </span>
                )}
            </button>
          ))}
        </div>

        {/* Table Container */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <tr>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">
                    <div className="flex items-center gap-2">
                      <User size={16} />
                      User
                    </div>
                  </th>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Rank</th>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Reward</th>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Amount</th>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="p-4 text-left text-sm font-semibold text-gray-700">Dates</th>
                  {tab === 'claimed' && (
                    <th className="p-4 text-left text-sm font-semibold text-gray-700">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={tab === 'claimed' ? 7 : 6} className="p-8 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-400">
                        <Shield size={48} className="mb-3 opacity-50" />
                        <p className="text-lg font-medium text-gray-500">No records found</p>
                        <p className="text-sm mt-1">Try adjusting your filters</p>
                      </div>
                    </td>
                  </tr>
                )}

                {rows.map((row) => (
                  <tr
                    key={`${row._id}-${row.rewardType}`}
                    className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center">
                          <Mail size={18} className="text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{row.email}</p>
                          <p className="text-sm text-gray-500">ID: {row._id.slice(-8)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <span className="font-medium text-gray-900">{row.rank}</span>
                    </td>
                    <td className="p-4">
                      <RewardTypeBadge type={row.rewardType} />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <DollarSign size={16} className="text-emerald-600" />
                        <span className="font-bold text-gray-900">₹{row.reward.rewardAmount}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <StatusBadge status={row.reward.status} />
                    </td>

                    <td className="p-4">
                      <div className="space-y-1 min-w-[200px]">
                        {/* Claimed Date - Show when isClaimed is true */}
                        {row.reward.isClaimed && row.reward.claimedDate && (
                          <DateInfo label="Claimed" date={row.reward.claimedDate} />
                        )}
                        {/* Approved Date - Show when status is approved */}
                        {row.reward.status === 'approved' && row.reward.approvedDate && (
                          <DateInfo label="Approved" date={row.reward.approvedDate} />
                        )}
                        {/* Last Updated */}
                        <DateInfo label="Last Updated" date={row.reward.updatedAt} />
                      </div>
                    </td>

                    {tab === 'claimed' && row.reward.status === 'processing' && (
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <button
                            disabled={actionLoading}
                            onClick={() => openConfirm(row, 'approve')}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white rounded-lg hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 transition-all duration-200"
                          >
                            <Check size={16} />
                            Approve
                          </button>
                          <button
                            disabled={actionLoading}
                            onClick={() => openConfirm(row, 'reject')}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-rose-500 to-rose-600 text-white rounded-lg hover:from-rose-600 hover:to-rose-700 disabled:opacity-50 transition-all duration-200"
                          >
                            <X size={16} />
                            Reject
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {loading && (
            <div className="p-12 text-center">
              <div className="inline-flex items-center gap-3 text-gray-600">
                <Loader2 className="animate-spin" size={24} />
                <span className="font-medium">Loading rewards...</span>
              </div>
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-gray-600">
            Showing page <span className="font-semibold text-gray-900">{page}</span> of{' '}
            <span className="font-semibold text-gray-900">{totalPages}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={18} />
              Previous
            </button>

            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`w-10 h-10 rounded-lg transition-colors ${page === pageNum
                      ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      {confirmOpen && selectedRow && confirmAction && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-3 rounded-xl ${confirmAction === 'approve'
                ? 'bg-emerald-100 text-emerald-600'
                : 'bg-rose-100 text-rose-600'
                }`}>
                {confirmAction === 'approve' ? (
                  <CheckCircle size={24} />
                ) : (
                  <XCircle size={24} />
                )}
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  Confirm {confirmAction.charAt(0).toUpperCase() + confirmAction.slice(1)}
                </h2>
                <p className="text-gray-600 text-sm">This action cannot be undone</p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center">
                  <Mail size={18} className="text-blue-600" />
                </div>
                <div>
                  <p className="font-medium text-gray-900">{selectedRow.email}</p>
                  <p className="text-sm text-gray-500">Reward: {selectedRow.rewardType.replace('Reward', '')}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                <div>
                  <p className="text-gray-500">Amount</p>
                  <p className="font-semibold">₹{selectedRow.reward.rewardAmount}</p>
                </div>
                <div>
                  <p className="text-gray-500">Current Status</p>
                  <StatusBadge status={selectedRow.reward.status} />
                </div>
              </div>

              {/* Show claimed date in modal */}
              {selectedRow.reward.claimedDate && (
                <div className="text-sm">
                  <p className="text-gray-500">Claimed On</p>
                  <p className="font-medium">{formatDate(selectedRow.reward.claimedDate)}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmOpen(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmAndExecute}
                disabled={actionLoading}
                className={`px-5 py-2.5 text-white rounded-xl font-medium flex items-center gap-2 transition-all ${confirmAction === 'approve'
                  ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700'
                  : 'bg-gradient-to-r from-rose-500 to-rose-600 hover:from-rose-600 hover:to-rose-700'
                  } disabled:opacity-50`}
              >
                {actionLoading && <Loader2 className="animate-spin" size={18} />}
                {confirmAction === 'approve' ? 'Approve Reward' : 'Reject Reward'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RankDashboard;